(() => {
  const channel = 'da-support-v2';
  const pending = new Map();
  const accepted = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif']);
  const maxBytes = 8 * 1024 * 1024;

  const nonce = () => globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  const bridgeFor = (endpoint) => {
    let frame = [...document.querySelectorAll('iframe.da-support-bridge')].find((item) => item.dataset.endpoint === endpoint);
    if (!frame) {
      frame = document.createElement('iframe');
      frame.className = 'da-support-bridge';
      frame.name = `da-support-bridge-${Math.random().toString(36).slice(2)}`;
      frame.dataset.endpoint = endpoint;
      frame.title = 'Secure support response';
      document.body.append(frame);
    }
    return frame;
  };

  const postToBridge = (endpoint, payload) => new Promise((resolve, reject) => {
    if (!endpoint) return reject(new Error('The secure case service is not configured.'));
    const frame = bridgeFor(endpoint);
    const requestNonce = payload.nonce || nonce();
    payload.nonce = requestNonce;
    payload.origin = location.origin;
    pending.set(requestNonce, { resolve, reject, timer: setTimeout(() => {
      pending.delete(requestNonce);
      reject(new Error('The secure support service did not respond.'));
    }, 30000) });

    const relay = document.createElement('form');
    relay.method = 'post';
    relay.action = endpoint;
    relay.target = frame.name;
    relay.hidden = true;
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'payload';
    input.value = JSON.stringify(payload);
    relay.append(input);
    document.body.append(relay);
    relay.submit();
    relay.remove();
  });

  addEventListener('message', (event) => {
    if (!event.data || event.data.channel !== channel || !event.data.nonce) return;
    if (event.origin !== 'https://script.google.com' && !event.origin.endsWith('.googleusercontent.com')) return;
    const request = pending.get(event.data.nonce);
    if (!request) return;
    clearTimeout(request.timer);
    pending.delete(event.data.nonce);
    request.resolve(event.data.result || { ok: false, message: 'The secure support response was empty.' });
  });

  const fileToPayload = (file) => new Promise((resolve, reject) => {
    if (!accepted.has(file.type)) return reject(new Error(`${file.name}: use JPEG, PNG, WebP, HEIC, or HEIF.`));
    if (file.size > maxBytes) return reject(new Error(`${file.name}: file exceeds 8 MB.`));
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(`${file.name}: the file could not be read.`));
    reader.onload = () => resolve({
      original_filename: file.name,
      content_type: file.type,
      size_bytes: file.size,
      base64: String(reader.result).split(',')[1] || ''
    });
    reader.readAsDataURL(file);
  });

  const formPayload = async (form) => {
    const data = new FormData(form);
    const payload = {};
    for (const [key, value] of data.entries()) {
      if (value instanceof File) continue;
      payload[key] = value;
    }
    payload.accuracy_confirmed = data.get('accuracy_confirmed') === 'true';
    payload.privacy_acknowledged = data.get('privacy_acknowledged') === 'true';
    payload.evidence = await Promise.all([...form.querySelectorAll('input[type="file"]')]
      .flatMap((input) => [...input.files]).map(fileToPayload));
    return payload;
  };

  const showErrors = (form, errors) => {
    const summary = form.querySelector('[data-da-error-summary]');
    if (!summary) return;
    summary.hidden = false;
    summary.replaceChildren();
    const heading = document.createElement('strong');
    heading.textContent = 'Fix this before filing:';
    const list = document.createElement('ul');
    errors.forEach((item) => {
      const row = document.createElement('li');
      row.textContent = item;
      list.append(row);
    });
    summary.append(heading, list);
    summary.focus();
  };

  const setupStructuredForm = (form, action) => {
    const submit = form.querySelector('[type="submit"]');
    const idempotency = form.querySelector('[name="idempotency_key"]');
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!form.checkValidity()) {
        showErrors(form, ['Correct the marked required fields.']);
        form.reportValidity();
        return;
      }
      if (idempotency && !idempotency.value) idempotency.value = nonce();
      submit.disabled = true;
      const original = submit.textContent;
      submit.textContent = action === 'create_claim' ? 'FILING…' : 'SENDING…';
      try {
        const payload = await formPayload(form);
        payload.action = action;
        const result = await postToBridge(form.action, payload);
        if (!result.ok) throw new Error(result.message || 'The secure support service rejected the request.');
        location.assign(result.confirmation_path);
      } catch (error) {
        showErrors(form, [error.message || 'Something failed. Try again. If it keeps failing, use general support.']);
        submit.disabled = false;
        submit.textContent = original;
      }
    });
  };

  const claimForm = document.querySelector('[data-da-claim-form]');
  if (claimForm) {
    const issue = claimForm.querySelector('[name="issue_type"]');
    const evidence = [...claimForm.querySelectorAll('[data-da-evidence]')];
    const rules = {
      damaged: { min: 2, note: 'Upload the full item and visible damage. Add packaging or label evidence when shipping damage is involved.' },
      print_quality: { min: 1, note: 'Lay the item flat and show the full design and defect clearly.' },
      print_placement: { min: 1, note: 'Lay the item flat and include a ruler or measuring tape.' },
      wrong_item: { min: 2, note: 'Show the full item and the visible tag or label.' },
      wrong_variant_fulfillment: { min: 2, note: 'Show the full item and the visible size or color tag.' },
      manufacturing_size_defect: { min: 1, note: 'Lay the garment flat and show the size-chart measurement.' },
      missing_item: { min: 1, note: 'Show the package or shipping label when available.' },
      other_fulfillment: { min: 1, note: 'Show the full item and the problem clearly.' }
    };
    const renderEvidence = () => {
      const rule = rules[issue?.value] || { min: 0, note: 'Photos are optional for this category unless support requests them.' };
      evidence.forEach((field, index) => {
        field.hidden = index >= Math.max(rule.min, 1);
        const input = field.querySelector('input[type="file"]');
        if (input) input.required = index < rule.min;
      });
      const note = claimForm.querySelector('[data-da-evidence-note]');
      if (note) note.textContent = rule.note;
    };
    issue?.addEventListener('change', renderEvidence);
    renderEvidence();
    setupStructuredForm(claimForm, 'create_claim');
  }

  const supportForm = document.querySelector('[data-da-support-form]');
  if (supportForm) setupStructuredForm(supportForm, 'create_general_support');

  const statusForm = document.querySelector('[data-da-status-form]');
  if (statusForm) statusForm.addEventListener('submit', async (event) => {
    event.preventDefault();
    const resultBox = document.querySelector('[data-da-status-result]');
    const button = statusForm.querySelector('[type="submit"]');
    button.disabled = true;
    resultBox.hidden = false;
    resultBox.textContent = 'CHECKING…';
    try {
      const payload = Object.fromEntries(new FormData(statusForm));
      payload.action = 'status';
      const result = await postToBridge(statusForm.action, payload);
      resultBox.replaceChildren();
      const heading = document.createElement('strong');
      heading.textContent = result.ok ? `${result.case_number}: ${result.status}` : 'Status unavailable';
      const detail = document.createElement('p');
      detail.textContent = result.next_step || result.message || 'We could not verify that case. Check the number and email.';
      resultBox.append(heading, detail);
    } catch {
      resultBox.textContent = 'Status is temporarily unavailable. Try again later.';
    } finally {
      button.disabled = false;
    }
  });

  const confirmation = document.querySelector('[data-da-confirmation]');
  if (confirmation) {
    const token = new URLSearchParams(location.search).get('token');
    const endpoint = confirmation.dataset.endpoint;
    if (!token || !endpoint) confirmation.textContent = 'This confirmation link is invalid or expired.';
    else postToBridge(endpoint, { action: 'confirmation', token })
      .then((result) => {
        confirmation.replaceChildren();
        const heading = document.createElement('h2');
        heading.className = 'da-heading';
        heading.textContent = result.ok ? result.case_number : 'CONFIRMATION UNAVAILABLE';
        const message = document.createElement('p');
        message.className = 'da-copy';
        message.textContent = result.ok
          ? `Filed for ${result.email_masked}. Initial review target: ${result.review_target}. Missing evidence can pause review.`
          : result.message;
        confirmation.append(heading, message);
        if (result.ok) {
          const link = document.createElement('a');
          link.className = 'da-button';
          link.href = result.status_url;
          link.textContent = 'CHECK CASE STATUS';
          confirmation.append(link);
        }
      })
      .catch(() => { confirmation.textContent = 'Confirmation is temporarily unavailable. Check the internal test outbox.'; });
  }
})();
